import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_PREFIX } from '../services/api';
import { useAuth } from '../utils/AuthContext';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  CircularProgress,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Work,
  People,
  Refresh,
  CloudUpload,
  Visibility,
  Add,
  Person,
  Business,
  Schedule,
  CheckCircle,
  CalendarToday as CalendarIcon,
  VideoCall,
  Cancel,
  Edit,
  AccessTime,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ArrowBack,
  ArrowForward,
  Delete,
  Assignment,
  Search,
  BusinessCenter,
} from '@mui/icons-material';
import CreateSingleSlot from '../components/common/CreateSingleSlot';
import TalentFlowCalendar from '../components/shared/TalentFlowCalendar';
import InterviewManagement from '../components/interview/InterviewManagement';
import AppShell from '../components/shared/AppShell';
import { Section, StatCard, EmptyState } from '../components/shared/ui';
import OptimizedCandidatePipeline from '../components/vendor/OptimizedCandidatePipeline';
import RequirementQueue from '../components/vendor/RequirementQueue';
import ResumeUploadForm from '../components/vendor/ResumeUploadForm';

// One source of truth for navigation — the shell renders both the desktop
// rail and the mobile drawer from this, so their indices cannot drift apart.
// The `tabValue === n` blocks in the render below are matched to these indices
// by hand, so adding an entry means renumbering them too.
const SECTIONS = [
  { label: 'Requirements', icon: <Assignment /> },
  { label: 'Job Management', icon: <BusinessCenter /> },
  { label: 'My Candidates', icon: <People /> },
  { label: 'Available Slots', icon: <CalendarIcon /> },
  { label: 'Interview Management', icon: <Schedule /> },
];

const VendorDashboard = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const [tabValue, setTabValue] = useState(0);
  
  // Handle navigation state for selectedTab (e.g., from notifications)
  useEffect(() => {
    if (location.state?.selectedTab !== undefined) {
      setTabValue(location.state.selectedTab);
    }
  }, [location.state]);

  const [dashboardData, setDashboardData] = useState({
    vendor_info: null,
    assigned_jobs: [],
    vendor_candidates: [],
    statistics: null,
    jobs: [], // All jobs for management
    loading: false,
    error: null,
    lastLoaded: null,
  });
  
  // Job Management State
  const [jobSearchTerm, setJobSearchTerm] = useState('');
  const [jobsStatusFilter, setJobsStatusFilter] = useState('all');
  const [jobsPage, setJobsPage] = useState(1);
  const jobsPerPage = 20;
  
  // Interview Management State
  const [bookedInterviews, setBookedInterviews] = useState([]);
  const [interviewManagement, setInterviewManagement] = useState({
    interviews: [],
    loading: false,
    error: null
  });

  // CV Upload Dialog State
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [slotDialog, setSlotDialog] = useState(false);

  // Interview Scheduling Dialog State
  const [interviewDialog, setInterviewDialog] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [panelists, setPanelists] = useState([]);
  const [allPanelists, setAllPanelists] = useState([]);
  const [selectedPanelist, setSelectedPanelist] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [scheduling, setScheduling] = useState(false);

  // Job Details Dialog State
  const [jobDetailsDialog, setJobDetailsDialog] = useState(false);
  const [selectedJobDetails, setSelectedJobDetails] = useState(null);

  // Interview Details Dialog State
  const [interviewDetailsDialog, setInterviewDetailsDialog] = useState(false);
  const [selectedInterviewDetails, setSelectedInterviewDetails] = useState(null);

  // Pagination state for candidates
  const [candidatesPagination, setCandidatesPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });

  // Navigation functions. Indices track the SECTIONS array above.
  const handleNavigateToJobs = () => {
    setTabValue(1);
  };

  const handleNavigateToCandidates = () => {
    setTabValue(2);
  };

  // Jobs Management Section Component
  const JobsManagementSection = () => {
    const filteredJobs = dashboardData.jobs.filter(job => {
      const matchesSearch = (job.title?.toLowerCase() || '').includes(jobSearchTerm.toLowerCase()) ||
        (job.location?.toLowerCase() || '').includes(jobSearchTerm.toLowerCase()) ||
        (job.job_code?.toLowerCase() || '').includes(jobSearchTerm.toLowerCase()) ||
        (job.job_reference?.toLowerCase() || '').includes(jobSearchTerm.toLowerCase());
      
      const matchesStatus = jobsStatusFilter === 'all' || job.status === jobsStatusFilter;
      
      return matchesSearch && matchesStatus;
    });

    const formatBudget = (job) => {
      if (job.formatted_budget && job.formatted_budget !== 'Not specified') {
        return job.formatted_budget;
      }
      
      if (job.budget_min || job.budget_max) {
        const currencySymbols = {
          'USD': '$', 'EUR': '€', 'GBP': '£', 'JPY': '¥', 'INR': '₹',
          'CAD': 'C$', 'AUD': 'A$', 'CHF': 'CHF', 'CNY': '¥', 'SGD': 'S$'
        };
        
        const symbol = currencySymbols[job.budget_currency] || job.budget_currency || '$';
        
        if (job.budget_min && job.budget_max) {
          const minFormatted = `${symbol}${parseInt(job.budget_min).toLocaleString()}`;
          const maxFormatted = `${symbol}${parseInt(job.budget_max).toLocaleString()}`;
          return `${minFormatted} - ${maxFormatted}`;
        } else if (job.budget_max) {
          return `Up to ${symbol}${parseInt(job.budget_max).toLocaleString()}`;
        } else if (job.budget_min) {
          return `From ${symbol}${parseInt(job.budget_min).toLocaleString()}`;
        }
      }
      
      return job.salary_range || 'Not specified';
    };

    const getBudgetDisplay = (job) => {
      const budgetText = formatBudget(job);
      const currency = job.budget_currency || 'USD';
      
      return {
        text: budgetText,
        currency: currency,
        isStructured: !!(job.budget_min || job.budget_max),
        isLegacy: !!(job.salary_range && !job.budget_min && !job.budget_max)
      };
    };

    const formatCreatedDate = (dateString) => {
      if (!dateString) return { date: 'N/A', time: '' };
      try {
        const date = new Date(dateString);
        return {
          date: date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short'
          }),
          time: date.toLocaleTimeString('en-GB', {
            hour: '2-digit',
            minute: '2-digit'
          })
        };
      } catch {
        return { date: 'N/A', time: '' };
      }
    };

    const getJobDisplayCode = (job) => {
      if (job.job_code && job.job_code.trim()) {
        return job.job_code;
      }
      return `JOB-${job.id}`;
    };

    const getStatusColor = (status) => {
      switch(status) {
        case 'open': return '#4caf50';
        case 'closed': return '#f44336';
        case 'filled': return '#2196f3';
        case 'on_hold': return '#ff9800';
        default: return '#757575';
      }
    };

    const handleViewJobDetails = (job) => {
      setSelectedJobDetails(job);
      setJobDetailsDialog(true);
    };

    return (
      <Card>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 2
          }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Job Management ({filteredJobs.length})
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search jobs..."
                value={jobSearchTerm}
                onChange={(e) => setJobSearchTerm(e.target.value)}
                sx={{ 
                  width: 200,
                  '& .MuiOutlinedInput-root': { height: 36 }
                }}
                InputProps={{
                  startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
              
              <FormControl size="small" sx={{ width: 110 }}>
                <Select
                  value={jobsStatusFilter}
                  onChange={(e) => setJobsStatusFilter(e.target.value)}
                  displayEmpty
                  sx={{ height: 36 }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="open">Open</MenuItem>
                  <MenuItem value="closed">Closed</MenuItem>
                  <MenuItem value="filled">Filled</MenuItem>
                  <MenuItem value="on_hold">On Hold</MenuItem>
                </Select>
              </FormControl>
              
              <Button
                variant="outlined"
                size="small"
                startIcon={<Refresh />}
                onClick={loadVendorDashboard}
                sx={{ height: 36 }}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              '& .MuiTableCell-root': {
                borderBottom: '1px solid #f0f0f0',
                py: 1,
                px: 1.5,
                verticalAlign: 'middle'
              }
            }}
          >
            <Table size="small" sx={{ minWidth: 1200 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '25%', textAlign: 'left' }}>
                    Job Code & Title
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '12%', textAlign: 'left' }}>
                    Location
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '15%', textAlign: 'left' }}>
                    Budget/Salary
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '8%', textAlign: 'center' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '8%', textAlign: 'center' }}>
                    Priority
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '8%', textAlign: 'center' }}>
                    My Candidates
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '10%', textAlign: 'center' }}>
                    Created
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem', width: '14%', textAlign: 'center' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredJobs
                  .slice((jobsPage - 1) * jobsPerPage, jobsPage * jobsPerPage)
                  .map((job) => {
                    const budgetInfo = getBudgetDisplay(job);
                    
                    return (
                      <TableRow 
                        key={job.id}
                        sx={{ '&:hover': { bgcolor: '#f8fafe' }, '&:nth-of-type(even)': { bgcolor: '#fafbfc' }, height: 50 }}
                      >
                        <TableCell sx={{ textAlign: 'left', verticalAlign: 'middle', py: 1, px: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1a1a', lineHeight: 1.3, mb: 0.25 }}>
                                {job.title}
                              </Typography>
                              {job.job_reference && (
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'block' }}>
                                  Ref: {job.job_reference}
                                </Typography>
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: '#666', bgcolor: '#f0f0f0', px: 0.5, py: 0.25, borderRadius: 0.5, fontWeight: 500, flexShrink: 0, border: '1px solid #e0e0e0' }}>
                              {getJobDisplayCode(job)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left', verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{job.location}</Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'left', verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: budgetInfo.isStructured ? 600 : 400, color: budgetInfo.isStructured ? '#1a1a1a' : 'text.secondary' }}>
                              {budgetInfo.text}
                            </Typography>
                            {budgetInfo.isStructured && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip label={budgetInfo.currency} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 500, '& .MuiChip-label': { px: 0.5 } }} />
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#4caf50' }}>Structured</Typography>
                              </Box>
                            )}
                            {budgetInfo.isLegacy && (<Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#ff9800' }}>Legacy Range</Typography>)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Chip label={job.status} size="small" sx={{ height: 22, fontSize: '0.7rem', textTransform: 'capitalize', bgcolor: getStatusColor(job.status), color: 'white', fontWeight: 500, minWidth: 60, '& .MuiChip-label': { px: 1 } }} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Chip label={job.priority} size="small" color={job.priority === 'high' || job.priority === 'urgent' ? 'error' : job.priority === 'medium' ? 'warning' : 'default'} sx={{ height: 22, fontSize: '0.7rem', minWidth: 60, '& .MuiChip-label': { px: 1 } }} />
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'primary.main', fontWeight: 600 }}>
                            {job.my_candidate_count || 0}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
                              {formatCreatedDate(job.created_at).date}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                              {formatCreatedDate(job.created_at).time}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="View Job Details">
                              <IconButton size="small" onClick={() => handleViewJobDetails(job)} sx={{ width: 28, height: 28, color: '#1976d2', '&:hover': { bgcolor: '#e3f2fd' } }}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Upload CV">
                              <IconButton size="small" onClick={() => { setSelectedJob(job); setUploadDialog(true); }} disabled={job.status === 'filled' || job.status === 'closed'} sx={{ width: 28, height: 28, color: '#00acc1', '&:hover': { bgcolor: '#e0f7fa' }, '&:disabled': { color: '#bdbdbd' } }}>
                                <CloudUpload fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                {filteredJobs.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No jobs found. Try adjusting your search or filter criteria.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredJobs.length > jobsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.75rem' }}>
                {((jobsPage - 1) * jobsPerPage) + 1}-{Math.min(jobsPage * jobsPerPage, filteredJobs.length)} of {filteredJobs.length}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" disabled={jobsPage === 1} onClick={() => setJobsPage(prev => prev - 1)} sx={{ width: 28, height: 28 }}>
                  <ArrowBack fontSize="small" />
                </IconButton>
                <Typography variant="caption" sx={{ px: 1, py: 0.5, bgcolor: '#f5f5f5', borderRadius: 0.5, fontSize: '0.75rem', minWidth: 40, textAlign: 'center' }}>
                  {jobsPage}/{Math.ceil(filteredJobs.length / jobsPerPage)}
                </Typography>
                <IconButton size="small" disabled={jobsPage === Math.ceil(filteredJobs.length / jobsPerPage)} onClick={() => setJobsPage(prev => prev + 1)} sx={{ width: 28, height: 28 }}>
                  <ArrowForward fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleSlotBooking = (slot) => {
    console.log('Booking slot:', slot);
  };

  const loadBookedInterviews = useCallback(async () => {
    try {
      setInterviewManagement(prev => ({ ...prev, loading: true }));
      const response = await fetch(`${API_PREFIX}/vendor/interviews`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      const data = await response.json();
      if (data.status === 'success') {
        setBookedInterviews(data.interviews);
        setInterviewManagement({ interviews: data.interviews, loading: false, error: null });
      }
    } catch (error) {
      console.error('Failed to load booked interviews:', error);
      setInterviewManagement({ interviews: [], loading: false, error: error.message });
    }
  }, []);

  const loadPanelistsForJob = async (jobId) => {
    try {
      const response = await fetch(`${API_PREFIX}/vendor/jobs/${jobId}/panelists`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      const data = await response.json();
      if (data.status === 'success') { setPanelists(data.panelists); } else { setPanelists([]); }
    } catch (error) { console.error('Failed to load panelists:', error); }
  };

  const loadAllPanelists = async () => {
    try {
      const response = await fetch(`${API_PREFIX}/vendor/panelists`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      const data = await response.json();
      if (data.status === 'success') { setAllPanelists(data.panelists); } else { setAllPanelists([]); }
    } catch (error) { console.error('Failed to load all panelists:', error); }
  };

  const openInterviewDialog = (candidate) => {
    setSelectedCandidate(candidate);
    setSelectedJob(dashboardData.assigned_jobs.find(job => job.id === candidate.job_id));
    loadPanelistsForJob(candidate.job_id);
    setSelectedPanelist(null);
    setSelectedSlot(null);
    setInterviewDialog(true);
  };

  const handleScheduleInterview = async () => {
    if (!selectedCandidate || !selectedPanelist || !selectedSlot) { return; }
    setScheduling(true);
    try {
      const response = await fetch(`${API_PREFIX}/vendor/interviews/schedule`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_id: selectedCandidate.id,
          panelist_id: selectedPanelist.id,
          job_id: selectedCandidate.job_id,
          scheduled_at: selectedSlot.start_datetime || `${selectedSlot.date}T${selectedSlot.start_time}`,
          duration_minutes: selectedSlot.duration_minutes || 30,
          interview_type: 'technical', location: '', meeting_link: ''
        }),
      });
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      const data = await response.json();
      if (data.status === 'success') {
        setInterviewDialog(false);
        setSelectedCandidate(null);
        setSelectedPanelist(null);
        setSelectedSlot(null);
        loadVendorDashboard();
        loadBookedInterviews();
      }
    } catch (error) {
      console.error('Failed to schedule interview:', error);
    } finally {
      setScheduling(false);
    }
  };

  const changeTabTo = (idx) => setTabValue(idx);

  const loadVendorDashboard = useCallback(async (candidatesPage = 1) => {
    setDashboardData(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await fetch(`${API_PREFIX}/vendor/dashboard?page=${candidatesPage}&per_page=20`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      const data = await response.json();
      setDashboardData({
        vendor_info: data.vendor_info,
        assigned_jobs: data.assigned_jobs || [],
        vendor_candidates: data.vendor_candidates || [],
        jobs: data.jobs || data.assigned_jobs || [],
        statistics: data.statistics || {},
        loading: false, error: null,
        lastLoaded: new Date().toLocaleTimeString(),
      });
      if (data.candidates_pagination) { setCandidatesPagination(data.candidates_pagination); }
    } catch (error) {
      console.error('Failed to load vendor dashboard:', error);
      const errorMessage = error.message.includes('401') ? 'Please login as a vendor to access this dashboard' : `Failed to load dashboard: ${error.message}`;
      setDashboardData(prev => ({ ...prev, loading: false, error: errorMessage }));
    }
  }, []);

  const loadJobDetails = async (jobId) => {
    try {
      const response = await fetch(`${API_PREFIX}/vendor/jobs/${jobId}`, { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) { throw new Error(`HTTP ${response.status}: ${response.statusText}`); }
      const data = await response.json();
      setSelectedJobDetails(data.job);
      setJobDetailsDialog(true);
    } catch (error) { console.error('Failed to load job details:', error); }
  };

  const viewInterviewDetails = (interview) => {
    setSelectedInterviewDetails(interview);
    setInterviewDetailsDialog(true);
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'vendor') {
      loadVendorDashboard();
      loadAllPanelists();
      loadBookedInterviews();
    }
  }, [isAuthenticated, user, loadVendorDashboard, loadBookedInterviews]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={28} sx={{ mb: 2 }} />
          <Typography variant="body1" color="text.secondary">Loading TalentMesh…</Typography>
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Not authenticated - redirecting to login...</Typography>
        <CircularProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (user?.role !== 'vendor') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Access denied - vendor role required</Typography>
      </Box>
    );
  }

  return (
    <AppShell
      role="vendor"
      sections={SECTIONS}
      activeIndex={tabValue}
      onSectionChange={changeTabTo}
      onRefresh={loadVendorDashboard}
      loading={dashboardData.loading}
      error={dashboardData.error}
    >
      {dashboardData.vendor_info ? (
        <>
          {dashboardData.statistics && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Jobs Assigned"
                  value={dashboardData.statistics.total_assigned_jobs || 0}
                  caption="Active assignments"
                  icon={<BusinessCenter />}
                  tone="info"
                  onClick={handleNavigateToJobs}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="My Candidates"
                  value={dashboardData.statistics.total_candidates_submitted || 0}
                  caption="Total pipeline"
                  icon={<People />}
                  tone="success"
                  onClick={handleNavigateToCandidates}
                />
              </Grid>
            </Grid>
          )}

          {tabValue === 0 && (
            <RequirementQueue
              onCandidateSubmitted={() => {
                loadVendorDashboard();
              }}
            />
          )}
          {tabValue === 1 && <JobsManagementSection />}
          {tabValue === 2 && (
            <OptimizedCandidatePipeline vendorId={user?.id} onNavigateToJobs={handleNavigateToJobs} />
          )}
          {tabValue === 3 && (
            <Section
              title="Panelist availability"
              subtitle="Select an open slot to book an interview"
              disablePadding
            >
              <TalentFlowCalendar
                userRole="vendor"
                height={700}
                onSlotBook={handleSlotBooking}
                bookedInterviews={bookedInterviews}
              />
            </Section>
          )}
          {tabValue === 4 && <InterviewManagement />}
        </>
      ) : (
        !dashboardData.loading && (
          <Section>
            <EmptyState
              icon={<Business />}
              title="No vendor profile found"
              description="Your account is not linked to a vendor profile yet. Contact an administrator to get set up."
            />
          </Section>
        )
      )}

      {/* Resume upload. Replaces the previous four-field dialog: the resume is
          parsed on attach and the details are filled in for the recruiter, with
          a warning if another recruiter already submitted this person. */}
      <ResumeUploadForm
        open={uploadDialog}
        onClose={() => setUploadDialog(false)}
        job={selectedJob}
        requirement={null}
        onSubmitted={() => {
          setTimeout(() => { loadVendorDashboard(); }, 300);
          setDashboardData(prev => ({
            ...prev,
            assigned_jobs: prev.assigned_jobs.map(job =>
              job.id === selectedJob?.id ? { ...job, candidate_count: (job.candidate_count || 0) + 1 } : job
            )
          }));
        }}
      />

      <Dialog open={interviewDialog} onClose={() => setInterviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Schedule />Schedule Interview{selectedCandidate && (<Chip label={selectedCandidate.name} color="primary" size="small" sx={{ ml: 1 }} />)}</Box></DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField select label="Select Panelist" value={selectedPanelist ? selectedPanelist.id : ''} onChange={(e) => { const panelist = panelists.find(p => p.id === parseInt(e.target.value)); setSelectedPanelist(panelist); setSelectedSlot(null); }} SelectProps={{ native: true }} fullWidth>
                  <option value="">-- Select Panelist --</option>
                  {panelists.map((panelist) => (<option key={panelist.id} value={panelist.id}>{panelist.username} ({panelist.expertise})</option>))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField select label="Select Time Slot" value={selectedSlot ? selectedSlot.id : ''} onChange={(e) => { const slot = selectedPanelist?.availability_slots.find(s => s.id === parseInt(e.target.value)); setSelectedSlot(slot); }} SelectProps={{ native: true }} fullWidth disabled={!selectedPanelist || selectedPanelist.availability_slots.length === 0}>
                  <option value="">-- Select Time Slot --</option>
                  {selectedPanelist?.availability_slots.map((slot) => {
                    let displayText = `Slot ${slot.id}`;
                    if (slot.start_datetime && slot.end_datetime) {
                      const startDate = new Date(slot.start_datetime); const endDate = new Date(slot.end_datetime);
                      displayText = `${startDate.toLocaleDateString()} ${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                    } else if (slot.date && slot.start_time && slot.end_time) {
                      displayText = `${slot.date} ${slot.start_time} - ${slot.end_time}`;
                    }
                    return (<option key={slot.id} value={slot.id}>{displayText}</option>);
                  })}
                </TextField>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setInterviewDialog(false)} variant="outlined" color="primary">Cancel</Button>
          <Button onClick={handleScheduleInterview} variant="contained" disabled={!selectedCandidate || !selectedPanelist || !selectedSlot || scheduling} startIcon={scheduling ? <CircularProgress size={20} /> : <Schedule />} sx={{ background: 'linear-gradient(45deg, #66bb6a 30%, #81c784 90%)', '&:hover': { background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)' } }}>{scheduling ? 'Scheduling...' : 'Schedule Interview'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={jobDetailsDialog} onClose={() => setJobDetailsDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Work />Job Details{selectedJobDetails && (<Chip label={selectedJobDetails.status || 'Open'} color="primary" size="small" sx={{ ml: 1 }} />)}</Box></DialogTitle>
        <DialogContent>
          {selectedJobDetails && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>{selectedJobDetails.title}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Description:</Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>{selectedJobDetails.description}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Requirements:</Typography>
                  <Typography variant="body1" sx={{ mb: 3 }}>{selectedJobDetails.requirements}</Typography>
                  {selectedJobDetails.panelist_info && (<Box sx={{ mt: 3 }}><Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>Assigned Panelist:</Typography><Chip label={selectedJobDetails.panelist_info.name} color="secondary" icon={<Person />} /></Box>)}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Job Information</Typography>
                    <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Location:</Typography><Typography variant="body2">{selectedJobDetails.location}</Typography></Box>
                    <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Salary Range:</Typography><Typography variant="body2">{selectedJobDetails.salary_range}</Typography></Box>
                    <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Priority:</Typography><Chip label={selectedJobDetails.priority || 'Medium'} size="small" color={selectedJobDetails.priority === 'high' ? 'error' : selectedJobDetails.priority === 'low' ? 'default' : 'warning'} /></Box>
                    <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">My Candidates:</Typography><Typography variant="body2">{selectedJobDetails.candidate_count || 0}</Typography></Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="caption" color="text.secondary">Created: {selectedJobDetails.created_at ? new Date(selectedJobDetails.created_at).toLocaleDateString() : 'N/A'}</Typography>
                  </Paper>
                  {selectedJobDetails.candidates && selectedJobDetails.candidates.length > 0 && (
                    <Paper sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>My Submitted Candidates</Typography>
                      <List dense>
                        {selectedJobDetails.candidates.map((candidate, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemAvatar><Avatar sx={{ bgcolor: '#5c6ac4', width: 32, height: 32 }}><Person /></Avatar></ListItemAvatar>
                            <ListItemText primary={candidate.name} secondary={<Box><Typography variant="caption" component="div">{candidate.email}</Typography><Chip label={candidate.current_stage?.replace('_', ' ') || 'Applied'} size="small" color={candidate.current_stage === 'hired' ? 'success' : candidate.current_stage === 'rejected' ? 'error' : candidate.current_stage === 'interview_scheduled' ? 'info' : 'default'} /></Box>} />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  )}
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJobDetailsDialog(false)}>Close</Button>
          {selectedJobDetails && selectedJobDetails.status !== 'filled' && (<Button variant="contained" startIcon={<CloudUpload />} onClick={() => { setJobDetailsDialog(false); setSelectedJob(selectedJobDetails); setUploadDialog(true); }} sx={{ background: 'linear-gradient(45deg, #00acc1 30%, #26c6da 90%)', '&:hover': { background: 'linear-gradient(45deg, #0097a7 30%, #00acc1 90%)' } }}>Upload CV for this Job</Button>)}
        </DialogActions>
      </Dialog>

      <Dialog open={interviewDetailsDialog} onClose={() => setInterviewDetailsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Schedule />Interview Details{selectedInterviewDetails && (<Chip label={selectedInterviewDetails.status} color={selectedInterviewDetails.status === 'completed' ? 'success' : selectedInterviewDetails.status === 'scheduled' ? 'info' : selectedInterviewDetails.status === 'cancelled' ? 'error' : 'default'} size="small" sx={{ ml: 1 }} />)}</Box></DialogTitle>
        <DialogContent>
          {selectedInterviewDetails && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Candidate Information</Typography>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Name:</Typography><Typography variant="body1">{selectedInterviewDetails.candidate_name}</Typography></Box>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Email:</Typography><Typography variant="body1">{selectedInterviewDetails.candidate_email}</Typography></Box>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Job:</Typography><Typography variant="body1">{selectedInterviewDetails.job_title}</Typography></Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Interview Details</Typography>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Date & Time:</Typography><Typography variant="body1">{new Date(selectedInterviewDetails.scheduled_at).toLocaleString()}</Typography></Box>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Duration:</Typography><Typography variant="body1">{selectedInterviewDetails.duration_minutes} minutes</Typography></Box>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Panelist:</Typography><Typography variant="body1">{selectedInterviewDetails.panelist.name}</Typography></Box>
                  <Box sx={{ mb: 2 }}><Typography variant="caption" color="text.secondary">Type:</Typography><Typography variant="body1">{selectedInterviewDetails.interview_type || 'General'}</Typography></Box>
                </Grid>
                {selectedInterviewDetails.meeting_link && (<Grid item xs={12}><Divider sx={{ my: 2 }} /><Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><Button variant="contained" color="primary" startIcon={<VideoCall />} onClick={() => window.open(selectedInterviewDetails.meeting_link, '_blank')}>Join Meeting</Button><Typography variant="caption" color="text.secondary">Meeting link will be available 15 minutes before the interview</Typography></Box></Grid>)}
                {selectedInterviewDetails.booking_notes && (<Grid item xs={12}><Divider sx={{ my: 2 }} /><Typography variant="h6" gutterBottom>Notes</Typography><Typography variant="body2" sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>{selectedInterviewDetails.booking_notes}</Typography></Grid>)}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInterviewDetailsDialog(false)}>Close</Button>
          {selectedInterviewDetails && selectedInterviewDetails.status === 'scheduled' && (
            <>
              <Button variant="outlined" color="warning" startIcon={<Edit />} onClick={() => { console.log('Reschedule interview:', selectedInterviewDetails.id); }}>Reschedule</Button>
              <Button variant="outlined" color="error" startIcon={<Cancel />} onClick={() => { console.log('Cancel interview:', selectedInterviewDetails.id); }}>Cancel</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* ✅ REMOVED: Floating Action Button for Quick CV Upload */}

      <CreateSingleSlot open={slotDialog} onClose={() => setSlotDialog(false)} panelists={allPanelists} onSlotCreated={() => { setSlotDialog(false); loadVendorDashboard(); }} />
    </AppShell>
  );
};

export default VendorDashboard;